import { createEvent } from "library/local-events/index.mjs"

/**
 * Raised when the local amends collection for a schedule changes (add, update, delete).
 * Scoped by draft id (`createDraftId(projectId, scheduleId)`).
 *
 * Consumers: `AmendsChanged(draftId).useEvent(refresh)` — re-read session/amends.
 *
 * Server comparison sync raises `{ source: "comparison-sync" }` only after
 * `sync-authoring-amends-comparisons` writes AuthoringAmends rows.
 */
export const AmendsChanged = createEvent("AmendsChanged")

/**
 * Raised when saveDraft completes successfully. Scoped by draft id.
 * Consumers: `SaveSucceeded(draftId).useEvent(...)` — e.g. useHasSavedEdits.
 */
export const SaveSucceeded = createEvent("SaveSucceeded")

/**
 * Raised when a restore point is created server-side after save.
 * Scoped by draft id. History tab: `RestorePointAdded(draftId).useEvent(refetch)`.
 */
export const RestorePointAdded = createEvent("RestorePointAdded")

/** Raised when draft macro token ids change (draft load, amends change). Scoped by draft id. */
export const DraftTokensChanged = createEvent("DraftTokensChanged")

/**
 * Raised when local draft content/structure changes in place (apply recommendation, add task, etc.).
 * Scoped by draft id. Consumers rebuild derived UI (recommendation applied state) without refetching.
 */
export const DraftStructureChanged = createEvent("DraftStructureChanged")

/**
 * Raised after debounced field edits with `{ scopes: string[] }` for targeted recommendation conflict checks.
 * Scoped by draft id. Consumers: `DraftEdited(draftId).useEvent(...)`.
 */
export const DraftEdited = createEvent("DraftEdited")

/** Raised after reloadAuthoringDraftFromServer completes successfully. Scoped by draft id. */
export const DraftReloaded = createEvent("DraftReloaded")

/**
 * Raised when session.hasSubstantiveEdits changes (initial compare or save-skip re-compare).
 * Consumers: `SubstantiveEditsChanged(draftId).useEvent(refresh)`.
 */
export const SubstantiveEditsChanged = createEvent("SubstantiveEditsChanged")

/**
 * Raised when schedule autosave dirty state changes (ToBeSaved for id "schedule").
 * Consumers: `SaveDirtyChanged(draftId).useEvent(refresh)`.
 */
export const SaveDirtyChanged = createEvent("SaveDirtyChanged")

/**
 * Raised after a Project Bridge recommendation's persisted "applied" flag is toggled
 * (set on Apply, cleared on Undo). Scoped by draft id.
 *
 * Consumers: the recommendations tab refetches the server recommendation list so an
 * applied Project Bridge suggestion is dropped server-side (or restored on undo),
 * instead of relying on live text-difference comparison to hide it.
 */
export const ProjectBridgeRecommendationApplied = createEvent("ProjectBridgeRecommendationApplied")

export const ScheduleCodeAvailabilityChanged = createEvent("ScheduleCodeAvailabilityChanged")

/**
 * Raised by Submit For Review to ask the open note editors which required notes the author has
 * emptied on screen. An empty note cannot be saved, so the server still sees the old one.
 * Scoped by draft id. Payload `{ amendsKeys: string[] }`: handlers push the keys they own.
 */
export const CollectUnsavedMissingAmends = createEvent("CollectUnsavedMissingAmends")
